





































































































































import { Component, Prop, PropSync, Vue, Watch } from "vue-property-decorator";
import rest from "@/rest";
// components
import TrainingUserSelect from "@/components/Academy/TrainingUserSelect.vue";
// interfaces
import { TrainingAppointmentVm, TrainingAppointmentStatus } from "@/interfaces/TrainingAppointmentVm";
import { TrainingLocationVm } from "@/interfaces/TrainingLocationVm";
import { TrainingUserVm } from "@/interfaces/TrainingUserVm";

interface SessionRow {
  key: number;
  id: number;
  title: string;
  start: string;
  end: string;
  locationId: number | null;
  isAttendanceRequired: boolean;
}

interface SpeakerRow {
  key: number;
  id: number;
  isInternal: boolean;
  user: TrainingUserVm | null;
  externalName: string;
  externalEmail: string;
  comment: string;
}

// Creates or edits an appointment with its days, speakers and responsibles. Emits "saved" with the saved appointment.
@Component({
  components: {
    TrainingUserSelect
  }
})
export default class TrainingAppointmentEditDialog extends Vue {
  @PropSync("showDialog") syncedShowDialog!: boolean;
  @Prop({ required: true, type: Number }) readonly orgId!: number;
  @Prop({ required: true, type: Number }) readonly eventId!: number;
  @Prop({ default: null }) readonly appointment!: TrainingAppointmentVm | null;
  @Prop({ default: () => [] }) readonly locations!: TrainingLocationVm[];

  valid = true;
  saving = false;
  nextKey = 1;

  name = "";
  capacity: string | null = "";
  meetingLink = "";
  notes = "";
  sessions: SessionRow[] = [];
  speakers: SpeakerRow[] = [];
  responsibles: TrainingUserVm[] = [];

  readonly nameRules = [(v: string) => (v || "").length <= 200 || "Maximal 200 Zeichen."];
  readonly linkRules = [(v: string) => (v || "").length <= 1000 || "Maximal 1000 Zeichen."];
  readonly requiredRules = [(v: string) => !!(v && String(v).trim()) || "Pflichtfeld"];
  readonly capacityRules = [(v: string | null) => v === null || v === "" || (/^\d+$/.test(String(v)) && Number(v) >= 1) || "Ganze Zahl ab 1."];

  @Watch("syncedShowDialog")
  onShowDialogChanged(show: boolean): void {
    if (show) this.reset();
  }

  reset(): void {
    const a = this.appointment;
    this.name = a?.name || "";
    this.capacity = a && a.capacity !== null ? String(a.capacity) : "";
    this.meetingLink = a?.meetingLink || "";
    this.notes = a?.notes || "";
    this.sessions = (a?.sessions || []).map(s => ({
      key: this.nextKey++,
      id: s.id,
      title: s.title || "",
      start: this.$globalHelper.toDateTimeInputValue(s.startDate),
      end: this.$globalHelper.toDateTimeInputValue(s.endDate),
      locationId: s.trainingLocationId,
      isAttendanceRequired: s.isAttendanceRequired
    }));
    if (this.sessions.length === 0) this.addSession();
    this.speakers = (a?.speakers || []).map(s => ({
      key: this.nextKey++,
      id: s.id,
      isInternal: !!s.portalUserId,
      user: s.portalUserId ? { id: s.portalUserId, fullName: s.displayName, userName: null } : null,
      externalName: s.externalName || "",
      externalEmail: s.externalEmail || "",
      comment: s.comment || ""
    }));
    this.responsibles = [...(a?.responsibles || [])];
    this.$nextTick(() => (this.$refs.form as any)?.resetValidation());
  }

  // New days start the day after the last one with the same times and room
  addSession(): void {
    const last = this.sessions[this.sessions.length - 1];
    let start = "";
    let end = "";
    if (last && last.start && last.end) {
      const s = new Date(last.start);
      const e = new Date(last.end);
      s.setDate(s.getDate() + 1);
      e.setDate(e.getDate() + 1);
      start = this.$globalHelper.toDateTimeInputValue(s);
      end = this.$globalHelper.toDateTimeInputValue(e);
    }
    this.sessions.push({
      key: this.nextKey++,
      id: 0,
      title: "",
      start,
      end,
      locationId: last ? last.locationId : null,
      isAttendanceRequired: true
    });
  }

  removeSession(index: number): void {
    this.sessions.splice(index, 1);
  }

  // Propose a one hour duration when the end is still empty
  onStartChanged(session: SessionRow): void {
    if (session.start && !session.end) {
      const e = new Date(session.start);
      e.setHours(e.getHours() + 1);
      session.end = this.$globalHelper.toDateTimeInputValue(e);
    }
  }

  addSpeaker(isInternal: boolean): void {
    this.speakers.push({ key: this.nextKey++, id: 0, isInternal, user: null, externalName: "", externalEmail: "", comment: "" });
  }

  // Active rooms and the room the session already uses
  locationItems(session: SessionRow): TrainingLocationVm[] {
    return this.locations.filter(l => l.isActive || l.id === session.locationId);
  }

  close(): void {
    this.syncedShowDialog = false;
  }

  async save(): Promise<void> {
    if (!(this.$refs.form as any).validate()) return;
    if (this.speakers.some(s => s.isInternal && !s.user)) {
      this.$store.commit("ux/SB_WARNING", { message: "Bitte einen internen Referenten auswählen oder die Zeile entfernen.", timeout: 4000 });
      return;
    }

    this.saving = true;
    try {
      const vm: TrainingAppointmentVm = {
        id: this.appointment?.id ?? 0,
        trainingEventId: this.eventId,
        name: this.name.trim() || null,
        status: this.appointment?.status ?? TrainingAppointmentStatus.Planned,
        capacity: this.capacity === null || this.capacity === "" ? null : Number(this.capacity),
        meetingLink: this.meetingLink.trim() || null,
        notes: this.notes.trim() || null,
        startDate: new Date(),
        endDate: new Date(),
        registeredCount: 0,
        waitlistCount: 0,
        sessions: this.sessions.map(s => ({
          id: s.id,
          order: 0,
          title: s.title.trim() || null,
          startDate: this.$globalHelper.fromDateTimeInputValue(s.start) as any,
          endDate: this.$globalHelper.fromDateTimeInputValue(s.end) as any,
          trainingLocationId: s.locationId,
          trainingLocationName: null,
          isAttendanceRequired: s.isAttendanceRequired
        })),
        speakers: this.speakers.map(s => ({
          id: s.id,
          portalUserId: s.isInternal ? s.user!.id : null,
          displayName: null,
          externalName: s.isInternal ? null : s.externalName.trim(),
          externalEmail: s.isInternal ? null : s.externalEmail.trim() || null,
          comment: s.comment.trim() || null
        })),
        responsibles: this.responsibles
      };

      // On errors the rest wrapper shows the message and returns undefined: keep the dialog open then
      const result: TrainingAppointmentVm | undefined = await rest.url("trainingLead/saveAppointment").post(vm);
      if (!result) return;
      this.syncedShowDialog = false;
      this.$emit("saved", result);
    } finally {
      this.saving = false;
    }
  }
}
